<script setup lang="ts">
import type { Portfolio } from "~/typings/portfolio";
import { computed } from "vue";

/**
 * Base ExperienceCard component
 * Clean, simple card displaying work experience information
 */
interface ExperienceCardProps extends Portfolio.Experience {}

const props = defineProps<ExperienceCardProps>();

const emit = defineEmits<{
  click: [];
}>();

const roleName = computed(() => props.roleName);

const handleClick = (event: Event) => {
  // Prevent click if clicking on external link
  const target = event.target as HTMLElement;
  if (target.closest("a")) {
    return;
  }
  emit("click");
};
</script>

<template>
  <UiCard
    tag="article"
    rounded="lg"
    class="experience-card"
    :aria-label="`Experience: ${company} - ${roleName}. Click for more details`"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Header -->
    <div class="flex justify-between gap-x-2">
      <div class="flex-1 min-w-0 flex flex-col gap-2">
        <!-- Role -->
        <UiTitle level="4" class="text-lg lg:text-xl font-bold">
          {{ roleName }}
        </UiTitle>

        <!-- Date -->
        <UiDatetimeLabel :start-date="startDate" :end-date="endDate" />
      </div>

      <!-- Ongoing Badge -->
      <UiOngoingBadge v-if="!endDate" />
    </div>

    <!-- Company & Client -->
    <UiTitle level="4" class="text-lg mt-4">
      <span class="font-bold" aria-label="Company">{{ company }}</span>
      -
      <span aria-label="Client">
        <UiAnchor
          v-if="clientUrl"
          :href="clientUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:underline"
        >
          {{ client ?? "Undisclosed" }}
        </UiAnchor>
        <template v-else>{{ client ?? " Undisclosed" }}</template>
      </span>
    </UiTitle>

    <!-- Description -->
    <UiParagraph class="mt-2">
      {{ shortDescription }}
    </UiParagraph>

    <!-- Technologies -->
    <ul
      v-if="technologies.length > 0"
      class="flex flex-wrap gap-2 mt-4"
      aria-label="Technologies used"
    >
      <UiTag v-for="(skill, idx) in technologies" :key="idx">
        {{ skill.name }}
      </UiTag>
    </ul>
  </UiCard>
</template>

<style scoped>
.experience-card {
  transition: border-color 0.3s ease-out;
}
</style>
